import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Flex,
  Box,
  Image,
  Text,
  useToast,
  Spinner,
  Grid,
  Link,
  Divider,
  Badge,
} from "@chakra-ui/react";
import CompetitorApi from "api/competitor";

const CompetitorSearchModal = ({ isOpen, onClose, productId, onMappingSuccess }) => {
  const toast = useToast();

  const [competitors, setCompetitors] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  
  const [isLoadingCompetitors, setIsLoadingCompetitors] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  
  const [groupedResults, setGroupedResults] = useState([]);
  const [mappingId, setMappingId] = useState(null);
  
  // استیت برای نگهداری محصولات مپ شده در این سشن، تا دکمه آنها سبز و غیرفعال شود
  const [mappedProductIds, setMappedProductIds] = useState([]);
  // استیت برای اینکه بدانیم آیا حداقل یک مپینگ موفق در این بار باز شدن مودال داشته‌ایم یا خیر
  const [hasMappedAny, setHasMappedAny] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchCompetitors();
      setSearchQuery("");
      setGroupedResults([]);
      setMappedProductIds([]);
      setHasMappedAny(false);
    }
  }, [isOpen]);

  const fetchCompetitors = async () => {
    setIsLoadingCompetitors(true);
    try {
      const response = await CompetitorApi.Competitors();
      setCompetitors(response.data || []);
    } catch (error) {
      toast({
        title: "خطا در دریافت لیست رقبا",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsLoadingCompetitors(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!searchQuery || searchQuery.length < 2) {
      toast({
        title: "حداقل ۲ کاراکتر برای جستجو وارد کنید",
        status: "warning",
        duration: 2000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    if (competitors.length === 0) {
      toast({
        title: "رقیب فعالی برای جستجو یافت نشد",
        status: "error",
        duration: 2000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    setIsSearching(true);
    setGroupedResults([]);

    try {
      const promises = competitors.map(async (comp) => {
        try {
          const res = await CompetitorApi.CompetitorsSearch({
            params: {
              competitor_id: comp.id,
              query: searchQuery,
            },
          });
          return {
            competitor: comp,
            products: res.data || [],
          };
        } catch (err) {
          return {
            competitor: comp,
            products: [],
            error: true,
          };
        }
      });

      const results = await Promise.all(promises);
      const activeResults = results.filter((group) => group.products.length > 0);
      setGroupedResults(activeResults);

    } catch (error) {
      toast({
        title: "خطا در جستجو",
        description: "مشکلی در دریافت اطلاعات پیش آمد.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handleMapProduct = async (competitorProductId, competitorId) => {
    setMappingId(competitorProductId);
    try {
      const response = await CompetitorApi.MapAttributes({
        competitor_id: competitorId,
        competitor_product_id: competitorProductId,
        product_id: productId,
      });

      toast({
        title: "تطبیق با موفقیت انجام شد",
        description: `تعداد ${response.data.mapped_count} واریانت مپ شد.`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });

      // اضافه کردن به لیست مپ شده‌ها
      setMappedProductIds(prev => [...prev, competitorProductId]);
      setHasMappedAny(true);
      
      // مودال بسته نمی‌شود!

    } catch (error) {
      toast({
        title: "خطا در عملیات تطبیق",
        description: error?.response?.data?.error || "خطایی رخ داده است.",
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setMappingId(null);
    }
  };

  // تابعی سفارشی برای بستن مودال: بررسی می‌کند اگر تغییری داشتیم، رفرش هم بکند
  const handleCloseModal = () => {
    if (hasMappedAny && onMappingSuccess) {
      onMappingSuccess();
    }
    onClose();
  };

  return (
    // با پاس دادن handleCloseModal به onClose در اینجا، کلیک روی Overlay (بیرون مودال) هم مدیریت می‌شود
    <Modal isOpen={isOpen} onClose={handleCloseModal} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent dir="rtl" maxW={{ base: "95%", lg: "900px" }} overflowX="hidden">
        <ModalHeader>جستجوی جامع در تمامی رقبا</ModalHeader>
        <ModalCloseButton right="unset" left="4" top="4" />
        
        <ModalBody pb={6} overflowX="hidden">
          <Box as="form" onSubmit={handleSearch} mb={6} p={4} borderWidth="1px" borderRadius="lg" bg="gray.50">
            <Grid templateColumns={{ base: "1fr", md: "1fr auto" }} gap={4} alignItems="end">
              <FormControl>
                <FormLabel fontSize="sm">نام محصول (بازی)</FormLabel>
                <Input
                  size="md"
                  bg="white"
                  placeholder="مثال: fc 26"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  isDisabled={isLoadingCompetitors}
                />
              </FormControl>

              <Button
                size="md"
                colorScheme="teal"
                type="submit"
                isLoading={isSearching || isLoadingCompetitors}
                px={10}
              >
                جستجو در همه
              </Button>
            </Grid>
          </Box>

          {isSearching && (
            <Flex direction="column" justify="center" align="center" py={10} gap={4}>
              <Spinner size="xl" color="teal.500" />
              <Text color="gray.500" fontSize="sm">در حال جستجو در تمامی سایت‌ها...</Text>
            </Flex>
          )}

          {!isSearching && groupedResults.length > 0 && (
            <Box>
              {groupedResults.map((group, index) => (
                <Box key={group.competitor.id} mb={index !== groupedResults.length - 1 ? 8 : 0}>
                  
                  <Flex align="center" mb={4} gap={3}>
                    <Badge colorScheme="purple" fontSize="sm" px={3} py={1} borderRadius="md">
                      {group.competitor.name}
                    </Badge>
                    <Text fontSize="xs" color="gray.500">
                      ({group.products.length} نتیجه)
                    </Text>
                    <Divider flex="1" borderColor="gray.300" />
                  </Flex>

                  <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                    {group.products.map((item) => {
                      const isMapped = mappedProductIds.includes(item.id);

                      return (
                        <Flex
                          key={item.id}
                          borderWidth="1px"
                          borderRadius="md"
                          p={3}
                          alignItems="center"
                          gap={4}
                          bg={isMapped ? "green.50" : "white"} // رنگ پس‌زمینه کارت بعد از مپ موفق
                          borderColor={isMapped ? "green.200" : "gray.200"}
                          shadow="sm"
                          overflow="hidden"
                        >
                          <Image
                            src={item.image || "https://via.placeholder.com/80"}
                            alt={item.name}
                            boxSize="50px"
                            objectFit="cover"
                            borderRadius="md"
                            borderWidth="1px"
                            flexShrink={0}
                          />
                          <Box flex="1" overflow="hidden" minW="0">
                            <Link
                              href={item.permalink}
                              isExternal
                              fontWeight="bold"
                              fontSize="sm"
                              color="blue.600"
                              display="block"
                              noOfLines={2}
                              title={item.name}
                              textAlign="right"
                              lineHeight="tall"
                              _hover={{ textDecoration: "underline", color: "blue.800" }}
                            >
                              {item.name}
                            </Link>
                            <Text fontSize="xs" color="gray.500" mt={1}>
                              ID: {item.id} | نوع: {item.type === "variable" ? "متغیر (دارای ظرفیت)" : "ساده"}
                            </Text>
                          </Box>
                          
                          <Button
                            size="sm"
                            colorScheme={isMapped ? "green" : "purple"}
                            variant={item.type === "variable" || isMapped ? "solid" : "outline"}
                            isDisabled={item.type !== "variable" || isMapped}
                            isLoading={mappingId === item.id}
                            onClick={() => handleMapProduct(item.id, group.competitor.id)}
                            flexShrink={0}
                          >
                            {isMapped ? "متصل شد" : "اتصال و مپ"}
                          </Button>
                        </Flex>
                      );
                    })}
                  </Grid>
                </Box>
              ))}
            </Box>
          )}

          {!isSearching && groupedResults.length === 0 && searchQuery && !isLoadingCompetitors && (
            <Flex justify="center" py={6}>
              <Text color="gray.500">هیچ محصولی در هیچ‌کدام از فروشگاه‌ها یافت نشد.</Text>
            </Flex>
          )}
        </ModalBody>

        <ModalFooter bg="gray.50" borderTopWidth="1px">
          <Button size="sm" onClick={handleCloseModal} variant="ghost">
            بستن
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CompetitorSearchModal;